import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Autocomplete, Box, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CustomButton from 'components/Button';
import DatePicker from 'components/DatePicker';
import Loader from 'components/Loader';
import { MessageType } from 'components/SnackBar';
import TimePicker from 'components/TimepickerNew/Timepicker';
import WarningInfo from 'components/WarningInfo';
import EditLeaveLog from 'features/EditLeaveLog';
import SaveAndExitModal from 'pages/EditQuote/Components/SaveAndExit';
import { RecentlyEditedLogType } from 'pages/WorkLog/WorkLog';
import { useGetCostEstimationByIdQuery, useUpdateWorklogMutation } from 'services/busmanApi';
import { ActivitiesProps, JobProps, PayrollCategoryTypes, WorklogType } from 'types/WorkLogTypes';
import { CategoriseActivities } from 'utils/activities';
import { DESCRIPTION_LIMIT } from 'utils/constants';
import { WORKLOG_TYPE } from 'utils/constants';
import { CalculateTotalHours } from 'utils/date';
import { getPreferncesFromLocalStorage, setPreferencesInLocalStorage } from 'utils/localStorage';
import { sortLists } from 'utils/sort';

import { RootState } from '../../store';

import { Header } from './styles';

interface ActivityOption {
  rate: number;
  status: string;
  groupType: string;
  activity_name: string;
  activity_id: string;
}

export interface ClientOptionType {
  client_id: string;
  client_name: string;
}

interface JobOptionType {
  job_id: string;
  job_name: string;
  job_number: string;
  job_description: string;
  job_type: string;
  client_id: string;
  job_status: string;
}

interface FormValuesProps {
  activity: ActivityOption | null;
  break_time: number;
  client: {
    client_id: string;
    im_client_id: string;
    client_name: string;
  };
  job: JobOptionType | null;
  payroll_category: PayrollCategoryTypes;
  date: Dayjs | null;
  description: string;
  endTime: Dayjs | null;
  startTime: Dayjs | null;
  worklogType: string;
}

interface EditWorklogProps {
  close: () => void;
  showMessage: (args: MessageType) => void;
  reset: () => void;
  updateApprovalList?: (ids: Array<string>, log?: WorklogType) => void;
  worklog: any;
  setRecentlyEdited?: (log: RecentlyEditedLogType) => void;
}

const getTooltipInfo = (groupName: string) => {
  switch (groupName) {
    case 'Matched':
      return 'Matched between your allowable activities and job allowable activities';
    case 'From Job':
      return 'Activities recommended for the selected job';
    case 'Your Activities':
      return 'Select only if matched activities are not correct for your selection';
    case 'Other Activity':
      return 'Select Other Activity';
    default:
      return '';
  }
};

const SearchActivitiesModal = React.lazy(() => import('../../components/SearchActivities'));

const EditWorklog = ({
  close,
  worklog,
  reset,
  updateApprovalList = () => {},
  showMessage,
  setRecentlyEdited,
}: EditWorklogProps) => {
  const theme = useTheme();
  const location = useLocation();
  const [updateWorklog, { isLoading }] = useUpdateWorklogMutation();
  const [recentClients, setRecentClients] = React.useState<ClientOptionType[]>([]);
  const [recentJobs, setRecentJobs] = React.useState<JobOptionType[]>([]);
  const [openSearchActivityModal, setOpenSearchActivityModal] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [formValues, setFormValues] = React.useState<FormValuesProps>({
    ...worklog,
    activity: worklog.activity ? { ...worklog.activity, groupType: '' } : null,
    date: dayjs(worklog?.date),
    endTime: dayjs(worklog?.end_time),
    startTime: dayjs(worklog?.start_time),
    worklogType: worklog.worklog_type,
  });
  dayjs.extend(isSameOrAfter);
  const worklogBilledSynced = worklog.status === 'Billed' || worklog.status === 'Synced';
  const { clients, activities, jobs } = useSelector((state: RootState) => state.worklogs);
  const { user } = useSelector((state: RootState) => state.auth);
  const isManagerView = location.pathname.startsWith('/employeeworklogs') || location.pathname.startsWith('/jobs');
  const logOwner = isManagerView ? worklog.employee : user;
  const [isOverEstimation, setOverEstimation] = React.useState<boolean>(false);
  const [showSaveAndExit, setShowAndExit] = React.useState<boolean>(false);
  const {
    data: estimationData,
    refetch,
    isUninitialized,
  } = useGetCostEstimationByIdQuery(formValues?.job?.job_id, {
    skip: formValues.job?.job_type !== 'Quoted' || !formValues.job?.job_id,
  });

  const {
    payroll_details: { leaves_category: leaveCategories, wages_category: wagesCategories },
  } = logOwner;

  const sortedLeaveCategories = [...leaveCategories].sort((a, b) => a.category_name.localeCompare(b.category_name));

  const activitiesList = React.useMemo(() => {
    if (!formValues.job) return [];

    const { activities: userActivities } = logOwner;
    return CategoriseActivities(activities, userActivities, jobs, formValues?.job.job_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagerView, logOwner, formValues.job]);

  const filteredActivities = activities.filter((activity) => {
    if (!formValues?.job) return activity;
    // Chargeable activity to be shown for Timebilled/Quoted Jobs
    const activityStatus = formValues.job?.job_type === 'Internal' ? 'NonChargeable' : 'Chargeable';
    return activity.status === activityStatus;
  });

  const formatDate = (value: Dayjs | null) => {
    return dayjs(value)
      .set('year', dayjs(formValues.date).get('year'))
      .set('month', dayjs(formValues.date).get('month'))
      .set('date', dayjs(formValues.date).get('date'))
      .format();
  };

  const totalLoggedHours = React.useMemo(() => {
    // Makes sure after edit selected times(start & end) are of same date
    // Handles use case of different dates in time when user inputs invalid time and then selects valid one
    const formattedStartTime = dayjs(formatDate(formValues?.startTime));
    const formattedEndTime = dayjs(formatDate(formValues?.endTime));
    return CalculateTotalHours(formattedStartTime, formattedEndTime, Number(formValues?.break_time));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.break_time, formValues?.endTime, formValues?.startTime]);

  const getClientList = () => {
    if (!clients.length) return [];

    const sortedClients: ClientOptionType[] = sortLists([...clients], 'client_name', 'client_id').map(
      ({ client_name, client_id }) => {
        return { client_name, client_id };
      },
    );
    if (recentClients.length) {
      const filteredSortedList = sortedClients.filter(
        (client) => !recentClients.some((recent) => recent.client_id === client.client_id),
      );
      return [...recentClients, ...filteredSortedList];
    }

    return sortedClients;
  };

  const clientsList: ClientOptionType[] = getClientList();

  const getJobList = () => {
    if (!jobs.length) return [];

    const filteredJobs =
      formValues.client !== null
        ? jobs?.filter(
            (job: JobProps) =>
              job.client?.client_id === formValues.client?.client_id && job.job_status === 'InProgress',
          )
        : jobs;

    const sortedJobs: JobOptionType[] = sortLists([...filteredJobs], 'job_number', 'job_id').map(
      ({ job_name, job_id, job_number, job_description, job_type, client, job_status }) => {
        return { job_name, job_id, job_number, job_description, job_type, client_id: client.client_id, job_status };
      },
    );

    if (recentJobs.length) {
      const filteredRecentJobPerClient =
        formValues.client !== null
          ? recentJobs?.filter((job: JobOptionType) => job.client_id === formValues.client?.client_id)
          : recentJobs;
      const filteredSortedList = sortedJobs.filter(
        (job) => !filteredRecentJobPerClient.some((recent) => recent.job_id === job.job_id),
      );
      return [...filteredRecentJobPerClient, ...filteredSortedList];
    }

    return sortedJobs;
  };

  const jobList = getJobList();

  const filterOptions = (options: ActivityOption[]) => {
    const isInternal = formValues.job?.job_type === 'Internal';
    const desiredStatus = isInternal ? 'NonChargeable' : 'Chargeable';

    return options.filter(
      (option) => option.activity_name === 'Select Other Activity' || option.status === desiredStatus,
    );
  };

  const checkActivity = () => {
    if (formValues.activity) {
      const selectedActivity = activitiesList?.find(
        (activity) => activity.activity_id === formValues.activity?.activity_id,
      );

      return selectedActivity?.groupType === 'Matched';
    }
    return true;
  };

  const updateRecentClientList = (selectedClient: ClientOptionType) => {
    const newRecentClients = [
      selectedClient,
      ...recentClients.filter((client) => client.client_id !== selectedClient.client_id),
    ];
    const trimmedRecentClients = newRecentClients.slice(0, 5);
    setRecentClients(trimmedRecentClients);
    setPreferencesInLocalStorage('recentClients', trimmedRecentClients);
  };

  const updateRecentJobList = (selectedJob: JobOptionType) => {
    const newRecentJobs = [selectedJob, ...recentJobs.filter((job) => job.job_id !== selectedJob.job_id)];
    const trimmedRecentJobs = newRecentJobs.slice(0, 5);
    setRecentJobs(trimmedRecentJobs);
    setPreferencesInLocalStorage('recentJobs', trimmedRecentJobs);
  };

  const handleChange = (key: string, value: string | number | null) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const handleAutoCompleteChange = (key: string, value: ActivityOption | PayrollCategoryTypes | null) => {
    if (value && 'activity_id' in value && value?.activity_id === 'other') {
      setOpenSearchActivityModal(true);
      return;
    }

    setFormValues({ ...formValues, [key]: value ? value : null });
  };

  const handleJobChange = (key: string, value: JobOptionType | null) => {
    // Update recent jobs
    if (value) updateRecentJobList(value);

    if (formValues?.client === null && formValues?.activity === null) {
      const selectedJob = jobList.find((job) => job.job_id === value?.job_id);
      const selectedClient = clients.find((client) => client.client_id === selectedJob?.client_id);

      const clientSelection = {
        client_id: selectedClient ? selectedClient.client_id : '',
        client_name: selectedClient ? selectedClient.client_name : '',
      };

      // Update recent client list
      updateRecentClientList(clientSelection);

      return setFormValues({
        ...formValues,
        [key]: value,
        ['activity' as string]: null,
        ['client' as string]: clientSelection,
      });
    }

    return setFormValues({ ...formValues, [key]: value, ['activity' as string]: null });
  };

  const handleClientChange = (key: string, value: ClientOptionType | null) => {
    setFormValues({ ...formValues, [key]: value, job: null, activity: null });
    // Update recent clients in localstorage
    if (value) {
      const newRecentClients = [value, ...recentClients.filter((client) => client.client_id !== value.client_id)];
      const trimmedRecentClients = newRecentClients.slice(0, 5);
      setRecentClients(trimmedRecentClients);
      setPreferencesInLocalStorage('recentClients', trimmedRecentClients);
    }
  };

  const hasValidDate = () => {
    if (!dayjs(formValues.date).isValid()) {
      setError('Error: Invalid Date');

      return false;
    }

    const currentDate = dayjs();
    const selectedDate = dayjs(formValues.date).format('YYYY-MM-DD');

    const diff = currentDate.diff(selectedDate, 'month');

    if (diff >= 12 || diff <= -12) {
      setError('Error: Date should be within 12 months');
      return false;
    }

    if (formValues.worklogType === WORKLOG_TYPE.WORKLOG && dayjs(formValues.date).isAfter(currentDate)) {
      setError('Error: Worklog date cannot be in the future');
      return false;
    }

    return true;
  };

  const hasValidTime = () => {
    if (!dayjs(formValues.startTime).isValid() || !dayjs(formValues.endTime).isValid()) {
      setError('Error: Invalid Time!');
      return false;
    }

    // Format both time to same date for correct result
    const startTime = formatDate(formValues.startTime);
    const endTime = formatDate(formValues.endTime);

    if (!dayjs(endTime)?.isSameOrAfter(dayjs(startTime))) {
      setError('Error: End time cannot be before start time !');
      return false;
    }
    return true;
  };

  const checkIsOverEstimation = React.useCallback(() => {
    if (!estimationData || !formValues.activity) return false;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { job_cost_projection, job_final_estimate } = estimationData;
    const rate = formValues.activity.rate;
    const baseCost = Number(job_cost_projection);
    const hourDiff = totalLoggedHours - worklog.worklog_hours;

    const adjustedCost = baseCost + hourDiff * rate;

    return adjustedCost > job_final_estimate;
  }, [estimationData, formValues.activity, totalLoggedHours, worklog.worklog_hours]);

  const submitWorklog = async () => {
    if (!hasValidDate()) return;

    if (!hasValidTime()) return;

    if (totalLoggedHours === 0) {
      setError('Error: No worklog hours has been logged !');
      return;
    }

    const formData = {
      activity_id: formValues.activity?.activity_id,
      break_time: formValues.break_time,
      client_id: formValues.client?.client_id,
      employee_id: logOwner.user_id,
      end_time: formatDate(formValues.endTime),
      job_id: formValues.job?.job_id,
      mapped_category_id: formValues.payroll_category?.mapped_category_id,
      slip_id: [worklog?.slip_id],
      start_time: formatDate(formValues.startTime),
      worklog_date: formValues.date?.format('YYYY-MM-DD'),
      worklog_description: formValues.description,
      worklog_hours: totalLoggedHours,
      worklog_type: worklog.worklog_type,
    };

    const result = await updateWorklog(formData);

    if ('error' in result) {
      setError('Error updating worklog!');
    }

    if ('data' in result) {
      updateApprovalList([worklog?.slip_id], result?.data?.data);
      reset();
      close();
      if (setRecentlyEdited)
        setRecentlyEdited({ date: result.data.data.date, slip_id: worklog?.slip_id, edited: true });
      showMessage({ type: 'success', msg: 'Worklog updated' });
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isOverEstimation) {
      setShowAndExit(true);
      return;
    }
    await submitWorklog();
  };

  const onSelectActivity = (activity: ActivitiesProps) => {
    handleAutoCompleteChange('activity', {
      activity_id: activity.activity_id,
      activity_name: activity.activity_name,
      status: activity.status,
      rate: Number(activity.rate) || 0,
      groupType: 'Other Activity',
    });
  };

  const setDate = (value: Dayjs | null) => {
    if (error) setError(null);
    setFormValues((prev) => ({ ...prev, date: value }));
  };

  const setStartTime = (value: Dayjs | null) => {
    if (error) setError(null);
    setFormValues((prev) => ({ ...prev, startTime: value }));
    if (!isUninitialized) {
      refetch();
    }
  };

  const setEndTime = (value: Dayjs | null) => {
    if (error) setError(null);
    setFormValues((prev) => ({ ...prev, endTime: value }));
    if (!isUninitialized) {
      refetch();
    }
  };

  React.useEffect(() => {
    const localRecentClients: ClientOptionType[] | [] = getPreferncesFromLocalStorage('recentClients') || [];
    const localRecentJobs = getPreferncesFromLocalStorage('recentJobs') || [];

    setRecentClients(localRecentClients);
    setRecentJobs(localRecentJobs);
  }, []);

  // Calculation for over-estimation
  React.useEffect(() => {
    if (estimationData) {
      setOverEstimation(checkIsOverEstimation());
    }
  }, [checkIsOverEstimation, estimationData]);

  if (worklog.worklog_type === WORKLOG_TYPE.LEAVELOG) {
    return (
      <EditLeaveLog
        close={close}
        refetch={reset}
        showMessage={showMessage}
        leaveLog={worklog}
        setRecentlyEdited={setRecentlyEdited}
        leaveCategories={sortedLeaveCategories}
        updateApprovalList={updateApprovalList}
      />
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '2px 2px 6px 0px rgba(0, 0, 0, 0.10)',
        borderRadius: '5px',
        backgroundColor: theme.palette.background.paper,
        height: '95svh',
        width: '500px',
        maxWidth: '500px',
        position: 'sticky',
        top: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          position: 'fixed',
          width: '100%',
          height: 'fit-content',
        },
        [theme.breakpoints.down('sm')]: {
          marginTop: '5dvh',
          width: '92%',
          maxWidth: '100%',
          height: '90dvh',
        },
      }}>
      <Loader open={isLoading} />
      <Header>
        <Typography variant="h4"> Edit {worklog.worklog_type} </Typography>
        <CloseIcon
          onClick={() => close()}
          sx={{
            color: '#9E9E9E',
            ':hover': {
              color: theme.palette.primary.main,
              cursor: 'pointer',
            },
          }}
        />
      </Header>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          DATE
        </Typography>
        <DatePicker onChange={setDate} value={formValues.date} />
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          CLIENT
        </Typography>
        <Autocomplete
          value={formValues.client}
          options={clientsList}
          groupBy={(option) =>
            recentClients?.some((client) => client.client_id === option?.client_id) ? 'Recent' : 'All'
          }
          getOptionLabel={(option) => `${option.client_name}`}
          isOptionEqualToValue={(option, value) => option.client_name === value.client_name}
          onChange={(event, value) => handleClientChange('client', value)}
          size="small"
          ListboxProps={{
            style: {
              fontSize: '12px',
            },
          }}
          popupIcon={<ExpandMoreIcon fontSize="small" />}
          renderInput={(params) => <TextField {...params} required />}
        />
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          CLIENT JOB NUMBER
        </Typography>
        <Autocomplete
          value={formValues.job}
          options={jobList}
          groupBy={(option) => (recentJobs?.some((job) => job.job_id === option?.job_id) ? 'Recent' : 'All')}
          getOptionLabel={(option) => `${option.job_number}`}
          isOptionEqualToValue={(option, value) => option.job_number === value.job_number}
          onChange={(event, value) => handleJobChange('job', value)}
          size="small"
          ListboxProps={{
            style: {
              fontSize: '12px',
            },
          }}
          popupIcon={<ExpandMoreIcon fontSize="small" />}
          renderInput={(params) => <TextField {...params} required />}
          renderOption={(props, option) => {
            // In recent MUI versions, key must be passed explicitly (not spread)
            // eslint-disable-next-line react/prop-types
            const { key, ...optionProps } = props;
            return (
              <Tooltip key={key} title={option?.job_description} placement="top">
                <li {...optionProps}>{option?.job_number}</li>
              </Tooltip>
            );
          }}
        />
        {formValues.job?.job_description && (
          <Box
            sx={{
              padding: theme.spacing(1),
              display: 'flex',
              alignContent: 'center',
              borderRadius: '2px',
              gap: '2px',
            }}>
            <InfoRoundedIcon
              sx={{ fontSize: '16px', color: theme.palette.busmanColors.busmanPrimary, mt: '1px', opacity: 0.7 }}
            />
            <Typography variant="h6" sx={{ color: theme.palette.busmanColors.busmanPrimary, opacity: 0.7 }} m={0}>
              <strong>{formValues.job?.job_name}:</strong> {formValues.job?.job_description}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          ACTIVITY ID
        </Typography>
        <Tooltip title={checkActivity() ? '' : 'Not recommended activity for this job or employee'} placement="top">
          <Autocomplete
            value={formValues.activity}
            options={activitiesList}
            filterOptions={filterOptions}
            groupBy={(option) => option?.groupType}
            getOptionLabel={(option) => `${option.activity_name}`}
            isOptionEqualToValue={(option, value) => option.activity_name === value.activity_name}
            onChange={(event, value) => handleAutoCompleteChange('activity', value)}
            size="small"
            sx={{
              backgroundColor: checkActivity() ? 'none' : theme.palette.busmanColors.busmanWarn,
            }}
            ListboxProps={{
              style: {
                fontSize: '12px',
              },
            }}
            popupIcon={<ExpandMoreIcon fontSize="small" />}
            renderInput={(params) => <TextField {...params} required />}
            renderOption={(props, option) => (
              <Tooltip title={getTooltipInfo(option.groupType)}>
                <li {...props}>{option.activity_name}</li>
              </Tooltip>
            )}
          />
        </Tooltip>
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          DESCRIPTION
        </Typography>
        <TextField
          FormHelperTextProps={{ style: { color: '#D9534F', margin: 0, textAlign: 'right' } }}
          helperText={`${formValues.description.length}/${DESCRIPTION_LIMIT}`}
          inputProps={{ maxLength: DESCRIPTION_LIMIT }}
          InputProps={{
            rows: 4,
            multiline: true,
            fullWidth: true,
            required: true,
          }}
          value={formValues.description}
          fullWidth
          name="description"
          onChange={(e) => handleChange('description', e.target.value)}
          sx={{ height: '100px' }}
        />
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          START TIME
        </Typography>
        <TimePicker onChange={setStartTime} value={formValues.startTime} />
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          END TIME
        </Typography>
        <TimePicker onChange={setEndTime} value={formValues.endTime} />
        {isOverEstimation && <WarningInfo description="Exceeded the estimated Quote" />}
        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
          BREAK(MINS)
        </Typography>
        <TextField
          fullWidth
          inputProps={{ maxLength: 4, step: '15', min: 0 }}
          name="breakTime"
          onChange={(e) => handleChange('break_time', Number(e.target.value))}
          size="small"
          type="number"
          value={formValues.break_time}
        />

        <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
          PAYROLL CATEGORY
        </Typography>
        <Autocomplete
          size="small"
          value={formValues.payroll_category}
          options={wagesCategories}
          getOptionLabel={(option: PayrollCategoryTypes) => `${option?.category_name}`}
          isOptionEqualToValue={(option, value) => option.category_name === value.category_name}
          onChange={(event, value) => handleAutoCompleteChange('payroll_category', value)}
          ListboxProps={{
            style: {
              fontSize: '12px',
            },
          }}
          popupIcon={<ExpandMoreIcon fontSize="small" />}
          renderInput={(params) => <TextField {...params} required />}
        />
      </Box>
      {error && (
        <Typography
          variant="subtitle2_italic"
          gutterBottom
          sx={{
            color: 'red',
            mb: 1,
            width: '100%',
            textAlign: 'center',
          }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          mx: 1,
          p: 1.5,
          display: 'flex',
          flexDirection: 'row',
          alignContent: 'flex-end',
          justifyContent: 'flex-end',
        }}>
        {totalLoggedHours > 0 && (
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{`Total Time Logged: ${totalLoggedHours} `}</Typography>
        )}
        <CustomButton variant="contained" type="submit" disabled={worklogBilledSynced} width="large" text="Submit" />
      </Box>
      <React.Suspense fallback={<Loader open={true} />}>
        <SearchActivitiesModal
          activities={filteredActivities}
          multipleSelection={false}
          open={openSearchActivityModal}
          onClose={() => setOpenSearchActivityModal(false)}
          onSelect={onSelectActivity}
        />
      </React.Suspense>
      {showSaveAndExit && (
        <SaveAndExitModal
          title={'Exceeded Estimated Quote!'}
          description={'Are you sure you wish to add the following worklog?'}
          onClose={() => {
            setOverEstimation(false);
            setShowAndExit(false);
            close();
          }}
          onExit={() => {
            setOverEstimation(false);
            setShowAndExit(false);
            close();
          }}
          onSave={() => {
            setOverEstimation(false);
            setShowAndExit(false);
            submitWorklog();
          }}
        />
      )}
    </Box>
  );
};

export default EditWorklog;
